import * as THREE from 'three';
import { type Labyrinth, TILE_SIZE, Tile } from '../labyrinth/Labyrinth';

interface PulseTile {
  x: number;
  y: number;
  /** Wall-time seconds when the current pulse started, or -1 if idle. */
  pulseStart: number;
  duration: number;
  /** Peak brightening multiplier for the red mask contribution. */
  peak: number;
  /** Wall-time seconds when the next pulse should kick off. */
  nextT: number;
}

/**
 * Per-wall-tile pulse intensity, exposed as a `mapWidth × mapHeight` R8
 * `DataTexture`. One byte per tile: 0 = no pulse, >0 = current brightening
 * factor. The wall shader samples this texture at the wall tile's centre
 * and scales the red-mask contribution by it — so each wall tile flashes
 * on its own random schedule even when it shares an atlas cell with others.
 */
export class WallPulseMap {
  readonly texture: THREE.DataTexture;
  readonly mapSize: THREE.Vector2;

  private readonly data: Uint8Array;
  private readonly tiles: PulseTile[] = [];
  private readonly width: number;
  private readonly height: number;

  constructor(lab: Labyrinth) {
    this.width = lab.width;
    this.height = lab.height;
    this.data = new Uint8Array(this.width * this.height);

    this.texture = new THREE.DataTexture(
      this.data, this.width, this.height, THREE.RedFormat,
    );
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.generateMipmaps = false;
    this.texture.needsUpdate = true;

    // Spread the initial "next pulse" times over 0–20s so walls don't all
    // fire their first flash in lockstep as soon as the map loads.
    for (let y = 0; y < lab.height; y++) {
      for (let x = 0; x < lab.width; x++) {
        if (lab.tiles[y]![x] !== Tile.Wall) continue;
        this.tiles.push({
          x, y,
          pulseStart: -1,
          duration: 0,
          peak: 1,
          nextT: Math.random() * 20,
        });
      }
    }

    this.mapSize = new THREE.Vector2(this.width * TILE_SIZE, this.height * TILE_SIZE);
  }

  update(timeSeconds: number): void {
    const d = this.data;
    d.fill(0);

    const w = this.width;
    for (const t of this.tiles) {
      if (t.pulseStart < 0 && timeSeconds >= t.nextT) {
        t.pulseStart = timeSeconds;
        t.duration = 0.22 + Math.random() * 0.28;
        t.peak = 1.6 + Math.random() * 1.1;
      }
      if (t.pulseStart < 0) continue;

      const u = (timeSeconds - t.pulseStart) / t.duration;
      if (u >= 1) {
        t.pulseStart = -1;
        // Rare flashes — next pulse 8–24s out keeps the world mostly quiet.
        t.nextT = timeSeconds + 8 + Math.random() * 16;
        continue;
      }

      // Sine envelope → 0..1 peak. Scale by (peak - 1) so the mask
      // contribution is purely additive (no flash = zero, not baseline).
      const env = Math.sin(u * Math.PI);
      const v = env * (t.peak - 1) * 180;
      d[t.y * w + t.x] = v > 255 ? 255 : v < 0 ? 0 : v | 0;
    }

    this.texture.needsUpdate = true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}
